import { useMemo } from "react";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  DocumentManagementDataType,
  MSADataType,
} from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import { useFileDOI } from "components/DOIHeaderSelect/useFileDOI";

type ClaimNumbersDisplayProps = {
  service:
    | MSADataType
    | CostProjectionDataType
    | AmendedReviewScreeningType
    | DocumentManagementDataType;
};

const NONE = "None";

export const ClaimNumbersDisplay = ({ service }: ClaimNumbersDisplayProps) => {
  // only amended review screening uses file-based DOI
  const isArScreening = service.type === SERVICE_TYPE.AR_SCREENING;
  const isFileBased = isArScreening;

  // use different hooks based on service type
  const fileHook = useFileDOI(isFileBased ? service.file : undefined);
  const serviceHook = useServiceDOI(
    isFileBased
      ? undefined
      : (service as
          | MSADataType
          | CostProjectionDataType
          | DocumentManagementDataType),
  );

  // for file hook, we need to use its selectedDoiId to filter claim numbers
  // for service hook, we can use the selectedDOI directly
  const selectedDOIId = isFileBased
    ? fileHook.selectedDoiId
    : serviceHook.selectedDOI?.id;

  const claimNumbers = useMemo(() => {
    const allClaimNumbers = service?.file?.claim_numbers || [];

    // if we have a selected DOI, filter by it
    if (selectedDOIId) {
      const filteredNumbers = allClaimNumbers
        .filter((claim) => claim.date_of_injury_id === selectedDOIId)
        .map((claim) => claim.number);

      if (filteredNumbers.length) {
        return filteredNumbers.join(", ");
      }
    }

    // fallback to all claim numbers if no DOI selected or no matches
    return allClaimNumbers.map((claim) => claim.number).join(", ") || NONE;
  }, [service?.file?.claim_numbers, selectedDOIId]);

  return <>{claimNumbers}</>;
};

export default ClaimNumbersDisplay;
